import React from "react";

import { useOnClickOutside } from "src/common/hooks/useOnClickOutside";
import { findScrollParent } from "src/common/utils/dom";

import "./Popover.css";

interface PopoverProps {
	target: React.ReactNode;
	content: React.ReactNode;
}

export const Popover: React.FunctionComponent<PopoverProps> = ({
	target,
	content
}) => {
	const containerRef = React.useRef<HTMLDivElement | null>(null);
	const contentRef = React.useRef<HTMLDivElement | null>(null);
	const [isOpen, setOpen] = React.useState(false);

	const onToggle = React.useCallback((event: React.MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		setOpen(isOpen => !isOpen);
	}, []);

	const onClose = React.useCallback(() => {
		setOpen(false);
	}, []);

	useOnClickOutside(containerRef, onClose);

	React.useEffect(() => {
		const { current: containerEl } = containerRef;
		if (containerEl === null) {
			return;
		}
		if (isOpen) {
			const parentEl = findScrollParent(containerEl);
			if (parentEl === null) {
				return;
			}
			const { current: contentEl } = contentRef;
			if (contentEl === null) {
				return;
			}
			const parentRect = parentEl.getBoundingClientRect();
			const containerRect = containerEl.getBoundingClientRect();
			const scrollHeight = parentEl.scrollHeight;
			const scrollTop = parentEl.scrollTop;
			containerEl.classList.add("popover--open");
			window.requestAnimationFrame(() => {
				const contentRect = contentEl.getBoundingClientRect();
				const top = Math.min(
					containerRect.height + 5,
					parentRect.top +
						scrollHeight -
						scrollTop -
						containerRect.top -
						contentRect.height -
						10
				);
				const left = Math.max(
					-contentRect.width + containerRect.width + 5,
					-containerRect.left + parentRect.left + 10
				);
				contentEl.style.top = `${top}px`;
				contentEl.style.left = `${left}px`;
			});
		} else {
			containerEl.classList.remove("popover--open");
		}
	}, [isOpen]);

	return (
		<div ref={containerRef} className="popover">
			<div role="button" className="popover__target" onClick={onToggle}>
				{target}
			</div>
			<div ref={contentRef} className="popover__content">
				{content}
			</div>
		</div>
	);
};
